import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { useAppMode, useFetchPendingTaskCount, usePendingTaskCount } from '../../stores/appStore'
import { businessSections, individualBottomItems, individualTopItems, type SidebarItemConfig } from '../navigation/navConfig'

function NavSection({ title }: { title: string }) {
  return (
    <div style={{ padding: '0.5rem 0.75rem', marginTop: '1rem', marginBottom: '0.25rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </div>
    </div>
  )
}

interface SidebarNavItemProps {
  to: string
  icon: LucideIcon
  label: string
  subtitle?: string
  collapsed: boolean
  badge?: number
  end?: boolean
  isActive?: (pathname: string) => boolean
  onClick?: () => void
}

function SidebarNavItem({ to, icon: Icon, label, subtitle, collapsed, badge, end, isActive: customIsActive, onClick }: SidebarNavItemProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => {
        const active = customIsActive ? customIsActive(window.location.pathname) : isActive
        return `nav-link ${active ? 'active' : ''}`
      }}
      onClick={onClick}
      style={{
        position: 'relative',
        justifyContent: collapsed ? 'center' : undefined,
        padding: collapsed ? '0.75rem' : '0.75rem 1rem',
      }}
      onMouseEnter={() => collapsed && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Icon size={20} aria-hidden="true" />
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span>{label}</span>
          {subtitle && (
            <span style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 400 }}>{subtitle}</span>
          )}
        </div>
      )}
      {!collapsed && badge != null && badge > 0 && (
        <span style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          background: '#f59e0b', color: 'black', fontSize: '0.7rem', fontWeight: '700',
          minWidth: '18px', height: '18px', borderRadius: '9px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
        }}>{badge}</span>
      )}
      {collapsed && badge != null && badge > 0 && (
        <span style={{
          position: 'absolute', top: '6px', right: '6px',
          width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b',
        }} />
      )}
      {collapsed && showTooltip && (
        <div style={{
          position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
          marginLeft: '8px', padding: '0.375rem 0.75rem', borderRadius: '6px',
          background: '#1f2937', color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 500,
          whiteSpace: 'nowrap', zIndex: 100, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {label}
        </div>
      )}
    </NavLink>
  )
}

interface NavigationProps {
  onNavigate?: () => void
  collapsed?: boolean
}

export function Navigation({ onNavigate, collapsed = false }: NavigationProps) {
  const mode = useAppMode()
  const pendingTaskCount = usePendingTaskCount()
  const fetchPendingTaskCount = useFetchPendingTaskCount()

  useEffect(() => {
    fetchPendingTaskCount()
    const interval = setInterval(fetchPendingTaskCount, 30000)
    return () => clearInterval(interval)
  }, [fetchPendingTaskCount])

  const handleClick = () => onNavigate?.()

  const resolveBadge = (item: SidebarItemConfig) => {
    if (item.badge === 'pendingTasks') return pendingTaskCount
    return undefined
  }

  if (mode === 'individual') {
    return (
      <nav aria-label="Main navigation" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ flex: 1 }}>
          {individualTopItems.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              subtitle={item.subtitle}
              collapsed={collapsed}
              badge={resolveBadge(item)}
              end={item.end}
              isActive={item.isActive}
              onClick={handleClick}
            />
          ))}
        </div>
        <div>
          {individualBottomItems.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              subtitle={item.subtitle}
              collapsed={collapsed}
              badge={resolveBadge(item)}
              end={item.end}
              isActive={item.isActive}
              onClick={handleClick}
            />
          ))}
        </div>
      </nav>
    )
  }

  return (
    <nav aria-label="Main navigation" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ flex: 1 }}>
        {businessSections.map((section) => (
          <div key={section.title}>
            <NavSection title={section.title} />
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={handleClick}
              >
                <item.icon size={20} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  )
}

